import { usePermissao } from "@/contexts/autorizacao/permissao";
import { FiltroTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/components/FiltroTermoNotificacao/FiltroTermoNotificacao";
import { ItemListFiltroTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/components/ItemListFiltroTermoNotificacao/ItemListFiltroTermoNotificacao";
import { ItemListTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/components/ItemListTermoNotificacao/ItemListTermoNotificacao";
import { SubmitBackupTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/components/SubmitBackupTermoNotificacao";
import { useTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/hooks/useTermoNotificacao";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { FilterOutlined, PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Col,
  Divider,
  Grid,
  List,
  Row,
  Tooltip,
  Typography,
} from "antd";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";

const PAGE_SIZE = 20;

export function ListaTermosNotificacao() {
  const { isOnline } = useNetworkStatus();
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { listaTermosNotificacao, getTermosNotificacaoApi } =
    useTermoNotificacao();
  const { hasEnabledPermissao } = usePermissao();
  const { message, notification } = App.useApp();
  const page = searchParams.get("page") || "1";

  const currentPage = Number(page);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginatedTermos = listaTermosNotificacao.slice(startIndex, endIndex);

  const {
    data: listaTermosFiltro,
    isLoading: isLoadingFiltro,
    refetch: refetchTermosFiltro,
    error: errorTermosFiltro,
  } = useQuery({
    queryKey: ["TERMOS_NOTIFICACAO_FILTRO", searchParams.toString()],
    enabled:
      isOnline &&
      searchParams.get("filtro") === "true" &&
      !!searchParams.get("estabelecimentoId"),

    queryFn: async () => {
      const response = await getTermosNotificacaoApi({
        estabelecimentoId: Number(searchParams.get("estabelecimentoId")),
        dataCadastro: searchParams.get("dataCadastro") || undefined,
        numero: searchParams.get("numero") || undefined,
        page: currentPage,
        status: searchParams.get("status") || undefined,
      });
      return response;
    },
  });

  const redirectToNovoTermoNotificacao = () => {
    navigate("/termos-notificacao/novo");
  };

  useEffect(() => {
    document.title = "Inspeção Mobile - Termos de Notificação";

    if (!isOnline && searchParams.get("filtro") === "true") {
      searchParams.delete("filtro");
      setSearchParams(searchParams);
    }

    if (errorTermosFiltro) {
      const messageError = formatMessageError(errorTermosFiltro);
      notification.error({
        message: "Não foi possivel carregar a lista de termos de notificação.",
        description: messageError,
        key: "error-filtro",
      });
    }
  }, [errorTermosFiltro]);

  return (
    <>
      <Typography.Title
        level={screens.xs ? 4 : 3}
        style={{ textAlign: "center" }}
      >
        Termos de Notificação
      </Typography.Title>

      <Row gutter={16} justify={"space-between"} align="middle">
        <Col>
          <Row gutter={8} align="middle">
            <Col>
              <Tooltip title="Filtro" destroyOnHidden>
                <Button
                  onClick={() => {
                    if (searchParams.get("filtro") === "true") {
                      searchParams.delete("filtro");
                      searchParams.delete("estabelecimentoId");
                      searchParams.delete("status");
                      searchParams.delete("dataCadastro");
                      searchParams.delete("numero");
                      searchParams.delete("page");
                      setSearchParams(searchParams);
                      message.info({
                        content:
                          "Filtro limpo, exibindo lista de termos de notificação sincronizados.",
                        key: "filtro",
                      });
                    } else {
                      searchParams.set("filtro", "true");
                    }
                    setSearchParams(searchParams);
                  }}
                  danger={searchParams.get("filtro") === "true"}
                >
                  <FilterOutlined />
                  {searchParams.get("filtro") === "true" && " Fechar"}
                </Button>
              </Tooltip>
            </Col>

            <Col>
              <Tooltip title="Novo Termo de Notificação">
                <Button
                  type="primary"
                  onClick={redirectToNovoTermoNotificacao}
                  disabled={!hasEnabledPermissao("TERMO_NOTIFICACAO_CRIAR")}
                >
                  <PlusOutlined />
                </Button>
              </Tooltip>
            </Col>
          </Row>
        </Col>

        <Col>
          <SubmitBackupTermoNotificacao />
        </Col>
      </Row>

      {searchParams.get("filtro") === "true" && (
        <Row>
          <Col span={24} style={{ marginTop: 16 }}>
            <FiltroTermoNotificacao />
          </Col>
        </Row>
      )}

      {searchParams.get("filtro") === "true" && (
        <>
          <Divider>
            <Typography.Text strong>Resultados da busca</Typography.Text>
          </Divider>
          <List
            size="small"
            bordered
            dataSource={listaTermosFiltro?.content || []}
            renderItem={(item, index) => (
              <ItemListFiltroTermoNotificacao
                key={index}
                item={item}
                reloadItems={refetchTermosFiltro}
              />
            )}
            loading={isLoadingFiltro}
            pagination={{
              pageSize: PAGE_SIZE,
              position: "bottom",
              defaultCurrent: 1,
              current: Number(page),
              total: listaTermosFiltro?.totalElements || 0,
              showSizeChanger: false,
              onChange: (page) => {
                searchParams.set("page", String(page));
                setSearchParams(searchParams);
              },
              align: "end",
              size: "small",
            }}
          />
        </>
      )}

      {!searchParams.get("filtro") && (
        <>
          <Divider>
            <Typography.Text strong>Termos sincronizados</Typography.Text>
          </Divider>

          <List
            size="small"
            bordered
            dataSource={paginatedTermos}
            renderItem={(item, index) => (
              <ItemListTermoNotificacao key={index} item={item} />
            )}
            pagination={{
              pageSize: PAGE_SIZE,
              position: "bottom",
              defaultCurrent: 1,
              current: Number(page),
              total: listaTermosNotificacao.length,
              onChange: (page) => setSearchParams({ page: String(page) }),
              showSizeChanger: false,
              align: "end",
              size: "small",
            }}
          />
        </>
      )}
    </>
  );
}
